import { getUID } from '@redviking/argonaut-util/src/getUID';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { FlowCfg, NodeCfg, NodeKind } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';

const validateAgainstCircularReferences = (flows: Record<string, FlowCfg>, flowCfgId: string, functionalFlowIds: string[]): string | null => {
    const flow = flows[flowCfgId];
    if (!flow) {
        return `Flow ID: ${flowCfgId} not found`;
    }

    for (const functionalSelectNode of Object.values(flow.nodes).filter((n): n is NodeCfg<'functionalSelect'> => n.kind === 'functionalSelect')) {
        if (functionalFlowIds.includes(functionalSelectNode.controlValues.flowId)) {
            return `Flow ${flow.name} is invloved in a circular functional select loop`;
        } else {
            const recursiveFlow = flows[functionalSelectNode.controlValues.flowId];
            if (!recursiveFlow) {
                return `Flow ID: ${functionalSelectNode.controlValues.flowId} not found`;
            }
            const recursiveCheck = validateAgainstCircularReferences(flows, recursiveFlow.id, [ ...functionalFlowIds, recursiveFlow.id ]);
            if (recursiveCheck) {
                return recursiveCheck;
            }
        }
    }
    return null;
};

export const functionalSelectUiNodeSpec: NodeSpec<NodeCfg<'functionalSelect'>> = {
    nodeCategory: 'function',
    kindLabel: 'Functional Select',
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.flowId) {
            errors.flowId = 'A flow selection is required';
        }
        const entityData = accessor.entityAsType('appletDesignVersion');
        const flows = entityData?.config.flows;
        if (!flows) {
            errors.flowId = 'No flow found';
        } else {
            const sourceFlowId = Object.values(flows).find(f => Object.values(f.nodes).find(n => n.id === node.id))?.id;
            if (!sourceFlowId) {
                errors.flowId = 'Source flow not found';
            } else {
                const result = validateAgainstCircularReferences(flows, sourceFlowId, [ sourceFlowId ]);
                if (result) {
                    errors.flowId = result;
                }
            }
        }
        return Object.keys(errors).length ? errors : true;
    },
    requiredSockets: (node, availableInputSockets, availableOutputSockets) => {
        const availableOptions = [
            'start',
            'finish',
            ...node.controlValues.inputs.map((_, index) => `input${index}`),
            ...node.controlValues.outputs.map((_, index) => `output${index}`),
        ] as ('start' | 'finish' | `input${number}` | `output${number}`)[];
        const availableInputs = new Set<string>(availableInputSockets);
        const availableOutputs = new Set<string>(availableOutputSockets);
        return {
            required: [],
            conditionallyRequired: [ {
                kind: 'or',
                availableOptions: availableOptions.filter(socket => socket === 'start' || availableInputs.has(socket) || availableOutputs.has(socket)),
            } ],
        };
    },
    cfg: () => ({
        id: getUID(),
        kind: 'functionalSelect',
        disabled: false,
        width: 260,
        controlValues: {
            flowId: '',
            inputs: [],
            outputs: [],
        },
    }),
    controls: nodeCfg => ({
        flowId: defineControlOpts({
            cfg: {
                kind: 'functionalFlowSelect',
                label: 'Select Function Flow',
            },
            on: {
                'update:value': (flowId, { flowEventEmitter }) => {
                    if (!flowId) {
                        return;
                    }
                    const flow = accessor.entityAsType('appletDesignVersion')?.config.flows[flowId];
                    const nodes = flow ? Object.values(flow.nodes as Record<string, NodeCfg<NodeKind>>) : [];
                    const inputsNode = nodes.find((node): node is NodeCfg<'functionalInputs'> => node.kind === 'functionalInputs');
                    const outputsNode = nodes.find((node): node is NodeCfg<'functionalOutputs'> => node.kind === 'functionalOutputs');
                    if (inputsNode && outputsNode) {
                        flowEventEmitter.emit('updateNodeCfg', { newNodeCfg: { ...nodeCfg, controlValues: { ...nodeCfg.controlValues, flowId, inputs: inputsNode.controlValues.inputs, outputs: outputsNode.controlValues.outputs } } });
                    }
                },
            },
            value: nodeCfg.controlValues.flowId,
        }),
    }),
    inputs: nodeCfg => Object.fromEntries([
        [ 'start', { socket: 'event' as const, label: 'Start' } ],
        ...nodeCfg.controlValues.inputs.map((input, index) => [ `input${index}`, { socket: input.dataType, label: input.name } ] as const),
    ]),
    outputs: nodeCfg => Object.fromEntries([
        [ 'finish', { socket: 'event' as const, label: 'Finish' } ],
        ...nodeCfg.controlValues.outputs.map((output, index) => [ `output${index}`, { socket: output.dataType, label: output.name } ] as const),
    ]),
};
